import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Progress } from "@volca/ui/components/ui/progress";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { TASK_STATE, tasksDone, type OnboardingTask } from "./onboarding-tasks";
import type { IntakeStep } from "./types";

const STATE_COLOR = {
  [TASK_STATE.done]: "text-success",
  [TASK_STATE.attention]: "text-warning",
  [TASK_STATE.todo]: "text-subtle",
} as const;

const TaskMark = ({ task, index }: { task: OnboardingTask; index: number }) => {
  if (task.state === TASK_STATE.done)
    return (
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-bold text-on-brand">
        <CheckIcon className="h-3.5 w-3.5" />
      </span>
    );
  if (task.state === TASK_STATE.attention)
    return (
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 border-warning-line text-warning">
        <span className="text-sm leading-none font-semibold">!</span>
      </span>
    );
  return (
    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-line">
      <Text weight="semibold" variant="caption" tone="muted">
        {index + 1}
      </Text>
    </span>
  );
};

/**
 * The same list in two sizes. On the welcome screen it is the page, so every task shows
 * what it is for. On a task screen it rides along at the side, where the description
 * would only repeat the heading the customer is already reading.
 */
export const OnboardingChecklist = ({
  tasks,
  currentStep,
  onPick,
  variant = "full",
  disabled = false,
  onViewAll,
}: {
  tasks: OnboardingTask[];
  currentStep: IntakeStep;
  onPick: (step: IntakeStep) => void;
  variant?: "full" | "compact";
  disabled?: boolean;
  /** Adds a footer row back to the full list. The full list has nowhere to go. */
  onViewAll?: () => void;
}) => {
  const full = variant === "full";
  const done = tasksDone(tasks);

  return (
    <Card className="w-full gap-0 py-0">
      <CardContent className="p-0">
        <Stack
          gap={1}
          align="stretch"
          className={cn(
            "w-full",
            cn({ "px-5 pt-3 pb-2": full, "px-4 pt-3 pb-2": !full }),
          )}
        >
          <Stack direction="row" justify="between" align="baseline" gap={2}>
            <Text
              variant={full ? "title" : "caption"}
              weight={full ? undefined : "semibold"}
            >
              Your checklist
            </Text>
            <Text variant="caption" tone="muted">
              {done} of {tasks.length} done
            </Text>
          </Stack>
          <Progress
            aria-label="Setup progress"
            aria-valuetext={`${done} of ${tasks.length} tasks completed`}
            value={tasks.length ? (done / tasks.length) * 100 : 0}
            className="*:data-[slot=progress-track]:bg-line"
          />
        </Stack>

        {/* gap={0}: the rows are a divided list, not spaced blocks. Stack's default
            16px gap left the hairlines floating and broke the current-task rail. */}
        <Stack className="w-full" gap={0} align="stretch">
          {tasks.map((task, index) => {
            const current = task.step === currentStep;
            return (
              <button
                key={task.step}
                type="button"
                onClick={() => onPick(task.step)}
                disabled={disabled || current}
                aria-label={`${task.label}, ${task.value}`}
                aria-current={current ? "step" : undefined}
                /* Where you are is a rail in the accent, not a fill. The marks in the
                   gutter already carry status, so a second background colour on the row
                   read as a third kind of state. Blue is left to mean "you can click
                   this", which the row you are standing on cannot be. */
                className={cn(
                  "relative flex w-full items-center gap-3 border-t border-line text-left transition-colors disabled:cursor-default",
                  {
                    "px-5 py-3": full,
                    "px-4 py-2.5": !full,
                    // -top-px pulls the rail over the row's own top border, which
                    // inset-y-0 would start below: an absolute child is placed against
                    // the padding box, so the rail came up a pixel short.
                    "before:absolute before:-top-px before:bottom-0 before:left-0 before:w-1 before:bg-brand-bold before:content-['']":
                      current,
                    "hover:bg-neutral-subtle-hovered": !current && !disabled,
                  },
                )}
              >
                <TaskMark task={task} index={index} />
                <Stack align="start" gap={0} className="min-w-0 flex-1">
                  <Text
                    variant={full ? undefined : current ? "label" : undefined}
                  >
                    {task.label}
                  </Text>
                  {full && task.description && (
                    <Text variant="caption" tone="muted">
                      {task.description}
                    </Text>
                  )}
                  {!full && (
                    <Text variant="caption" className={STATE_COLOR[task.state]}>
                      {task.value}
                    </Text>
                  )}
                </Stack>
                {full && (
                  <Stack direction="row" gap={2} align="center">
                    <Text variant="caption" className={STATE_COLOR[task.state]}>
                      {task.value}
                    </Text>
                    <CaretRightIcon className="h-4 w-4 shrink-0 text-subtle" />
                  </Stack>
                )}
              </button>
            );
          })}
          {onViewAll && (
            <button
              type="button"
              onClick={onViewAll}
              disabled={disabled}
              className="w-full border-t border-line bg-neutral px-4 py-3 text-left text-link disabled:cursor-default"
            >
              <Text weight="semibold" variant="caption" tone="link">
                View full checklist
              </Text>
            </button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
